/** Reconciliation questions: a file disagrees with your records, a parent wasn't found, a tag is shared. */
import { useState } from "react";
import { Link } from "react-router";
import { api, qs } from "../../../web/lib/api";
import { timeAgo } from "../../../web/lib/format";
import { t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { RefPicker } from "../../../web/forms/pickers";
import { Button, Card, Chip, Empty, ErrorNote, Field, Loading, toast } from "../../../web/ui/kit";
import type { IssueDTO } from "../shared/types";

export function IssueList({ importId }: { importId?: string }) {
  useLanguage();
  const q = useApi<{ items: IssueDTO[] }>(`/m/data/issues${qs({ status: "open", import: importId })}`);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  if (!q.data?.items.length) return <Empty icon="done" title={t("Nothing to settle")} body={t("When a file disagrees with your records, or a parent isn't found, it shows up here.")} />;
  return <div className="stack">{q.data.items.map((i) => <IssueCard key={i.id} issue={i} showImport={!importId} onDone={() => void q.refetch()} />)}</div>;
}

function IssueCard({ issue, showImport, onDone }: { issue: IssueDTO; showImport: boolean; onDone: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pick, setPick] = useState<string | null>(null);
  const act = async (action: string, target_id?: string) => {
    setBusy(action);
    setError(null);
    try {
      await api.post(`/m/data/issues/${issue.id}`, { action, target_id });
      toast(t("Resolved"), { tone: "good" });
      await invalidateAll();
      onDone();
    } catch (err) { setError(err); } finally { setBusy(null); }
  };
  const link = issue.target ? (issue.target.kind === "animal" ? `/cattle/${issue.target.id}` : issue.target.kind === "place" ? `/ranch/places/${issue.target.id}` : `/history/${issue.target.id}`) : null;
  const who = issue.target && link ? <Link to={link}>{issue.target.label}</Link> : null;
  const relation = issue.detail.relation === "dam" ? t("Dam") : issue.detail.relation === "sire" ? t("Sire") : null;
  return (
    <Card className="data-row-card">
      {issue.kind === "conflict" && (
        <>
          <strong>{t("Your records and the file disagree")}</strong>
          <p>{who}{issue.field_label ? ` · ${t(issue.field_label)}` : ""}</p>
          <dl className="data-list">
            <div><dt>{t("In Hondo now")}</dt><dd>{issue.local_value ?? t("Blank")}</dd></div>
            <div><dt>{t("The file says")}</dt><dd>{issue.source_value ?? t("Blank")}</dd></div>
          </dl>
          <div className="data-row-actions">
            <Button variant="primary" busy={busy === "keep_local"} onClick={() => void act("keep_local")}>{t("Keep mine")}</Button>
            <Button busy={busy === "use_source"} onClick={() => void act("use_source")}>{t("Use the file's")}</Button>
          </div>
        </>
      )}
      {issue.kind === "unmatched_parent" && (
        <>
          <strong>{t("Parent not found")}</strong>
          <p>{t("{{relation}} of {{animal}} is written as {{raw}}, which isn't an animal in your herd.", { relation: relation ?? t("Parent"), animal: issue.target?.label ?? "", raw: issue.detail.raw ?? issue.source_value ?? "" })}</p>
          <Field label={t("It's this animal")}><RefPicker target="animal" value={pick} onChange={(v) => setPick(v as string | null)} /></Field>
          <div className="data-row-actions">
            <Button variant="primary" disabled={!pick} busy={busy === "link"} onClick={() => void act("link", pick!)}>{t("Link them")}</Button>
            <Button busy={busy === "reference"} onClick={() => void act("reference")}>{t("Add as a reference ancestor")}</Button>
            <Button variant="ghost" busy={busy === "dismiss"} onClick={() => void act("dismiss")}>{t("Leave it unlinked")}</Button>
          </div>
          <p className="muted">{t("A reference ancestor shows in the family tree but never counts as part of the herd.")}</p>
        </>
      )}
      {issue.kind === "duplicate_tag" && (
        <>
          <strong>{t("Tag {{tag}} is on more than one animal", { tag: issue.detail.tag ?? issue.source_value ?? "" })}</strong>
          <p>{relation ? t("{{relation}} of {{animal}} could be any of these.", { relation, animal: issue.target?.label ?? "" }) : t("Hondo keeps them as separate animals. Retag one, or merge them if they're the same animal.")}</p>
          <div className="data-choices">{(issue.detail.candidates ?? []).map((c) => (
            relation
              ? <Button key={c.id} busy={busy === `link:${c.id}`} onClick={() => void act("link", c.id)}>{t("It's {{animal}}", { animal: c.label })}</Button>
              : <Link key={c.id} to={`/cattle/${c.id}`} className="chip tone-neutral">{c.label}</Link>
          ))}</div>
          <div className="data-row-actions"><Button variant="ghost" busy={busy === "dismiss"} onClick={() => void act("dismiss")}>{t("That's fine")}</Button></div>
        </>
      )}
      <p className="muted">
        {showImport && <Link to={`/ranch/data/imports/${issue.import_id}`}>{issue.import_title}</Link>}{showImport ? " · " : ""}{timeAgo(issue.created_at)}
        {issue.kind === "conflict" && <> · <Chip tone="neutral">{t("We kept yours until you choose")}</Chip></>}
      </p>
      {error ? <ErrorNote error={error} /> : null}
    </Card>
  );
}
